<template>
    <div>
        <header id="header" :class="{'header--compact': !nav }">
            <a
                href="#"
                class="hamburger hamburger--spring"
                v-bind:class="{ 'is-active': mobileNavOpen }"
                v-on:click.stop.prevent="openNav"
                v-if="nav"
            >
                <div class="hamburger-box">
                    <div class="hamburger-inner"></div>
                </div>
            </a>

            <router-link to="/dashboard" id="header__logo">
                <img src="/images/logo/logo-nav.png" alt="Logo" />
            </router-link>

            <nav id="header__nav" v-if="nav" v-bind:class="{ mobileNavOpen: mobileNavOpen }">
                <ul class="nav nav--stacked">
                    <li>
                        <router-link @click.native="resetState()" to="/dashboard">Dashboard</router-link>
                    </li>
                    <li v-if="can('viewOpportunities')">
                        <router-link @click.native="resetState()" to="/opportunities">Opportunities</router-link>
                    </li>
                    <li v-if="can('viewMADDs')">
                        <router-link @click.native="resetState()" to="/madds">MADDs</router-link>
                    </li>
                    <li v-if="can('viewInsights')">
                        <router-link @click.native="resetState()" to="/documents">Documents</router-link>
                        <ul class="submenu" v-if="showDocumentSubmenu">
                            <li v-if="can('createInsights')">
                                <router-link @click.native="resetState()" :to="{ name: 'create-document' }" class="admin">
                                    - Add Document
                                </router-link>
                            </li>
                            <li v-if="can('createInsightCategories')">
                                <router-link @click.native="resetState()" :to="{ name: 'create-document-category' }" class="admin">
                                    - Add Category
                                </router-link>
                            </li>
                            <li v-if="can('deleteInsightCategories')">
                                <router-link @click.native="resetState()" :to="{ name: 'delete-document-categories' }" class="admin">
                                    - Delete Category
                                </router-link>
                            </li>
                        </ul>
                    </li>
                    <li v-if="can('viewSectors')">
                        <router-link @click.native="resetState()" to="/sectors">
                            Sectors
                            <span
                                v-if="notificationCount > 0"
                                class="notification-count"
                                v-text="`+${notificationCount}`"
                            />
                        </router-link>
                    </li>
                    <li v-if="can('viewMembers')">
                        <router-link @click.native="resetState()" to="/members">Members</router-link>
                    </li>
                    <li v-if="can('viewUsers')">
                        <router-link @click.native="resetState()" to="/users" class="admin">- Users</router-link>
                    </li>
                    <li v-if="can('viewUsers')">
                        <router-link @click.native="resetState()" to="/reports" class="admin">- Reports</router-link>
                    </li>
                    <li v-if="can('impersonateUsers') && !impersonating">
                        <router-link @click.native="resetState()" to="/assume-identity" class="admin">
                            - Assume Identity
                        </router-link>
                    </li>
                    <li v-else-if="impersonating">
                        <a @click.prevent="leaveImpersonation(event)" class="admin">
                            {{ $wait.is('leaveImpersonation') ? 'Reverting...' : '- Revert Identity' }}
                        </a>
                    </li>
                    <li>
                        <router-link @click.native="resetState()" to="/search">Search</router-link>
                    </li>
                    <li v-if="can('viewEvents')">
                        <router-link @click.native="resetState()" to="/events">Events</router-link>
                    </li>
                    <li class="c-event-link" id="header__event" v-if="event">
                        <div class="c-event-link__content">
                            <h2 class="c-event-link__header">{{ event.eventName }}</h2>
                            <p class="c-event-link__copy">{{ event.timeRange }}</p>
                            <p class="c-event-link__copy" v-if="this.$store.getters.registrationCompleted">
                                Thanks for registering
                            </p>
                            <router-link
                                v-else class="btn btn--primary c-event-link__btn"
                                @click.native="resetState()"
                                to="/register/event"
                            >
                                Sign up
                            </router-link>
                        </div>
                    </li>
                    <li id="header__account">
                        <router-link @click.native="resetState()" to="/account">
                            <img :src="`/images/avatar/${user.id}`" alt="">
                            <span>My Account</span>
                        </router-link>
                    </li>
                </ul>
            </nav>
            <div id="header__secondary">
                <div id="header__search">
                    <HeaderSearch :typeahead="true" :default-value="$route.params.query" />
                </div>
                <Notifications
                    :count="notificationCount"
                    @updated="getNotificationCount"
                />
            </div>
        </header>
        <main id="content" ref="main-content" :class="{ 'container': true, 'has-fixed-group': hasFixedGroup }">
            <slot></slot>
        </main>
        <v-dialog />
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { mapWaitingActions } from 'vue-wait';
import HeaderSearch from '../Header/HeaderSearch';
import Notifications from '@components/Header/Notifications.vue';
import API from '@api';

export default {
    name: 'LayoutBase',
    components: {
        Notifications,
        HeaderSearch,
    },
    props: {
        nav: {
            type: Boolean,
            default: true,
        },
        breadcrumbs: {
            type: Array,
            default: () => [],
        },
        hasFixedGroup: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            notificationCount: 0,
            mobileNavOpen: 0,
        }
    },
    computed: {
        ...mapState({
            user: state => state.auth.user,
            impersonating: state => state.auth.impersonating,
            event: state => state.event,
        }),
        ...mapGetters([
            'can',
        ]),
        showDocumentSubmenu: function() {
            return this.can('createInsights') || this.can('createInsightCategories') || this.can('deleteInsightCategories')
        }
    },
    methods: {
        getNotificationCount: async function() {
            const { count } = await API.getNotificationCount();
            this.notificationCount = count ?? 0;
        },
        ...mapWaitingActions([
            'leaveImpersonation',
        ]),
        openNav() {
            this.mobileNavOpen = (this.mobileNavOpen) ? 0 : 1;
        },
        resetState() {
            this.$store.commit('resetState', null);
        },
    },
    watch: {
        '$route': 'getNotificationCount'
    },
    mounted: function() {
        this.getNotificationCount();
    },
}
</script>
<style lang="scss" scoped>
    @import '~@/theme/_variables.scss';

    .notification-count {
        //position: absolute;
        //top: -.4rem;
        //left: calc(50% + .4rem);
        margin-left: .4rem;
        background-color: $accent;
        color: white;
        font-family: Axiforma, sans-serif;
        font-size: 1rem;
        line-height: 1.2rem;
        border-radius: .4rem;
        padding: 0.2rem 0.5rem 0.1rem;
    }
</style>
<style lang="scss">
@import '~reflex-grid/scss/includes/variables';
@import '~reflex-grid/scss/includes/mixins';
@import '~@/theme/_variables.scss';
@import '~@/mixins/_class.scss';
@import '~@/theme/_extenables.scss';

#content {
    &:after {
        content: '';
        display: block;
        height: 3rem;
        flex: 0 0 auto;
    }

    &.has-fixed-group {
        &:after {
            height: 9rem;
        }
    }
}

#header__search {
    max-width: 30rem;
    flex-grow: 1;
}

#header__secondary {
    justify-content: flex-end;
}

#header__help {
    @include respond-to(medium) {
        display: none;
    }
}

.nav {
    > li ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    @media screen and (min-width: 991px) {
        > li {
            .submenu {
                display: none;
            }

            a.router-link-active + .submenu {
                display: flex;
                flex-direction: column;
            }
        }
    }
}
</style>
